import React, { useState, useEffect, useRef } from 'react';
import './Catalogue.css';
import { db } from '../services/firebase';
import { collection, getDocs, query, where, doc, updateDoc, getDoc, addDoc } from 'firebase/firestore';
import PageHeader from '../components/PageHeader';
import { useSeller } from '../contexts/SellerContext';
import  { getProductOptions } from './SellerSegment';
import { useNavigate } from 'react-router-dom';
import '../pages/LotDrawer.css';

const Openmarket = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  console.log(seller)
  const navigate = useNavigate();

  // State Management
  const [products, setProducts] = useState([]);
  const [operatorIds, setOperatorIds] = useState([]);
  const [pendingAcceptId, setPendingAcceptId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Drawer States
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [lotSizes, setLotSizes] = useState([{ size: '', set: '', grossWt: '', netWt: '', avgGrossWt: '', avgNetWt: '', avgSpecWt: '' }]);
  const sizeRowRefs = useRef({});




  const getProductDisplayInfo = (product) => {
    if (product.lotDetails && product.lotDetails.length > 0) {
      const totalSets = product.lotTotals?.totalSets ?? product.lotDetails.reduce((sum, item) => sum + (parseFloat(item.set) || 0), 0);
      const totalNetWt = product.lotTotals?.totalNetWt ?? product.lotDetails.reduce((sum, item) => sum + (parseFloat(item.netWt) || 0), 0);
      const totalGrossWt = product.lotTotals?.totalGrossWt ?? product.lotDetails.reduce((sum, item) => sum + (parseFloat(item.grossWt) || 0), 0);

      const sizes = product.lotDetails.map(item => item.size).filter(Boolean);
      const uniqueSizes = [...new Set(sizes)];

      return {
        displayWeight: `${totalNetWt.toFixed(3)}g`,
        displaySets: `${totalSets} sets`,
        displaySizes: uniqueSizes.join(', '),
        isLot: true,
        totalSets,
        totalNetWt,
        totalGrossWt
      };
    } else {
      return {
        displayWeight: `${product.netWt || 0}g`,
        displaySets: product.instockSet ? `${product.instockSet} sets` : '',
        displaySizes: Array.isArray(product.sizes) ? product.sizes.join(', ') : product.sizes || '',
        isLot: false
      };
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingProduct(null);
    setLotSizes([{ size: '', set: '', grossWt: '', netWt: '', avgGrossWt: '', avgNetWt: '', avgSpecWt: '' }]);
  };

  const getSizeOptions = () => {
    if (!editingProduct) return [];

    const segment = editingProduct.segment?.toUpperCase();
    const category = editingProduct.category;
    const source = editingProduct.productSource;
    const productName = editingProduct.productName?.toUpperCase();

    let options = getProductOptions(segment, category, source, productName);

    const existingInLot = lotSizes.map(item => item.size).filter(Boolean);
    const existingInProduct = Array.isArray(editingProduct.sizes)
      ? editingProduct.sizes
      : (typeof editingProduct.sizes === 'string' ? editingProduct.sizes.split(',').map(s => s.trim()) : []);

    const combined = Array.from(new Set([...options, ...existingInLot, ...existingInProduct])).filter(Boolean);
    return combined;
  };

  const calculateItemAverages = (item) => {
    const set = parseFloat(item.set) || 0;
    const grossWt = parseFloat(item.grossWt) || 0;
    const netWt = parseFloat(item.netWt) || 0;
    const spec = editingProduct?.specification || 'PLANE';

    if (set > 0) {
      const specWt = spec !== 'PLANE' ? Math.max(0, grossWt - netWt) : 0;
      return {
        avgGrossWt: (grossWt / set).toFixed(3),
        avgNetWt: (netWt / set).toFixed(3),
        avgSpecWt: (specWt / set).toFixed(3)
      };
    } else {
      return {
        avgGrossWt: item.avgGrossWt || (grossWt > 0 ? grossWt.toFixed(3) : '0.000'),
        avgNetWt: item.avgNetWt || (netWt > 0 ? netWt.toFixed(3) : '0.000'),
        avgSpecWt: item.avgSpecWt || '0.000'
      };
    }
  };

  const calculateTotals = () => {
    return {
      totalSets: lotSizes.reduce((sum, item) => sum + (parseFloat(item.set) || 0), 0),
      totalGrossWt: lotSizes.reduce((sum, item) => sum + (parseFloat(item.grossWt) || 0), 0),
      totalNetWt: lotSizes.reduce((sum, item) => sum + (parseFloat(item.netWt) || 0), 0)
    };
  };

  const setSizeRowRef = (index, element) => {
    if (element) {
      sizeRowRefs.current[index] = element;
    }
  };

  const handleSizeChange = (index, field, value) => {
    const newSizes = [...lotSizes];
    const updatedItem = { ...newSizes[index], [field]: value };

    if (field === 'grossWt' || field === 'netWt') {
      const gross = parseFloat(field === 'grossWt' ? value : updatedItem.grossWt);
      const net = parseFloat(field === 'netWt' ? value : updatedItem.netWt);
      if (!isNaN(gross) && !isNaN(net) && gross > 0 && net > gross) {
        alert('Net Weight cannot be greater than Gross Weight.');
        return;
      }
    }

    newSizes[index] = updatedItem;

    if (field === 'set' || field === 'grossWt' || field === 'netWt') {
      const averages = calculateItemAverages(newSizes[index]);
      newSizes[index] = {
        ...newSizes[index],
        avgGrossWt: averages.avgGrossWt,
        avgNetWt: averages.avgNetWt,
        avgSpecWt: averages.avgSpecWt
      };
    }

    setLotSizes(newSizes);
  };

  const handleSaveData = async () => {
    if (!editingProduct) return;

    for (let i = 0; i < lotSizes.length; i++) {
      const item = lotSizes[i];
      const gross = parseFloat(item.grossWt) || 0;
      const net = parseFloat(item.netWt) || 0;
      if (net > gross) {
        alert(`Net weight (${item.netWt}g) cannot be greater than Gross weight (${item.grossWt}g) for ${item.size ? 'size ' + item.size : 'row ' + (i + 1)}.`);
        return;
      }
    }

    try {
      const isPlane = editingProduct.specification === 'PLANE';
      const productRef = doc(db, 'products', editingProduct.id);

      const updatedLotSizes = lotSizes.map(item => {
        const set = parseFloat(item.set) || 0;
        const gross = parseFloat(item.grossWt) || 0;
        const net = parseFloat(item.netWt) || 0;
        return {
          size: item.size || '',
          set: (item.set !== undefined && item.set !== null) ? item.set.toString() : '0',
          grossWt: item.grossWt?.toString() || '0',
          netWt: item.netWt?.toString() || '0',
          avgGrossWt: set > 0 ? (gross / set).toFixed(3) : (item.avgGrossWt || '0.000'),
          avgNetWt: set > 0 ? (net / set).toFixed(3) : (item.avgNetWt || '0.000'),
          avgSpecWt: !isPlane ? (set > 0 ? ((gross - net) / set).toFixed(3) : (item.avgSpecWt || '0.000')) : '0.000'
        };
      });

      const totals = calculateTotals();
      const newSizes = updatedLotSizes.map(item => item.size).filter(Boolean);
      const specWt = !isPlane ? (totals.totalGrossWt - totals.totalNetWt).toFixed(3) : '0';

      const updatePayload = {
        serviceType: 'ready',
        lotDetails: updatedLotSizes,
        lotTotals: totals,
        grossWt: totals.totalGrossWt.toFixed(3),
        netWt: totals.totalNetWt.toFixed(3),
        instockGram: totals.totalGrossWt.toFixed(3),
        instockSet: totals.totalSets.toString(),
        sizes: newSizes,
        specificationWt: specWt
      };

      await updateDoc(productRef, updatePayload);
      alert('Product updated and moved to Ready Stock successfully!');
      closeDrawer();
    } catch (error) {
      console.error('Error updating stock details:', error);
      alert('Error updating stock details. Please try again.');
    }
  };


  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`, { state: { product } });
  };

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        setLoading(true);

        // 1. Fetch the seller's linked operator(s) from segment registration
        let linkedOperators = [];
        if (sellerId) {
          const segRef = doc(db, 'SellerSegmentRegistrations', sellerId);
          const segSnap = await getDoc(segRef);
          if (segSnap.exists()) {
            linkedOperators = segSnap.data().operators || [];
          }
        }
        console.log(linkedOperators);
        setOperatorIds(linkedOperators);

        const productsRef = collection(db, 'products');
        const q = query(
          productsRef,
          where('serviceType', '==', 'openmarket'),
          where('status', '==', 'approved')
        );
        const querySnapshot = await getDocs(q);

        const productsList = [];
        querySnapshot.forEach((docSnap) => {
          productsList.push({ id: docSnap.id, ...docSnap.data() });
        });

        // Sort client-side by timestamp/createdAt (desc) to avoid index needs
        productsList.sort((a, b) => {
          const ta = a.timestamp?.seconds || a.createdAt?.seconds || 0;
          const tb = b.timestamp?.seconds || b.createdAt?.seconds || 0;
          return tb - ta;
        });
        console.log(productsList)
        // 3. Show only products tied to the seller's linked operator(s)
        const filtered = linkedOperators.length > 0
          ? productsList.filter((p) => linkedOperators.includes(p.operatorId))
          : [];

        setProducts(filtered);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    };

    fetchAllProducts();
  }, [sellerId]);

  // Accept an open-market product: move it into orderList as an order in Preparation
  const handleAcceptProduct = async (product) => {
    if (!sellerId) {
      alert('Seller information not found. Please login again.');
      return;
    }
    if (pendingAcceptId) return;

    setPendingAcceptId(product.id);
    console.log(product)
    try {
      const orderData = {
        ...product,
        sellerId: sellerId,
        serviceType: 'order',
        orderStatus: 'Requested',
        selectedVariants: product.selectedVariants ||
          (product.lotDetails
            ? product.lotDetails.map((l) => ({ size: l.size, set: l.set }))
            : []),
      };
      console.log(orderData)
      await addDoc(collection(db, 'orderList'), orderData);


      setProducts((prev) => prev.filter((p) => p.id !== product.id));

      alert('✅ Product accepted and moved to Preparation (Order).');
    } catch (error) {
      console.error('Error accepting product:', error);
      alert('Failed to accept product. Please try again.');
    } finally {
      setPendingAcceptId(null);
    }
  };


  // ========== RENDER COMPONENTS ==========

  const renderLotDrawerContent = () => {
    const totals = calculateTotals();

    return (
      <>
        <div className="size-input-section">
          <h4>{editingProduct.productName} - {editingProduct.specification}</h4>
          {lotSizes.map((item, index) => (
            <div
              key={index}
              className="size-row"
              ref={el => setSizeRowRef(index, el)}
              data-row-index={index}
            >
              <div className="form-group">
                <label>Size</label>
                <select
                  value={item.size}
                  onChange={(e) => handleSizeChange(index, 'size', e.target.value)}
                  data-row-index={index}
                >
                  <option value="">Select Size</option>
                  {getSizeOptions().map(size => (
                    <option
                      key={size}
                      value={size}
                      disabled={lotSizes.some((item, i) => i !== index && item.size === size)}
                    >
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Set</label>
                <input
                  type="number"
                  value={item.set}
                  onChange={(e) => handleSizeChange(index, 'set', e.target.value)}
                  data-row-index={index}
                />
              </div>

              <div className="form-group">
                <label>Gross Wt. (g)</label>
                <input
                  type="number"
                  step="0.001"
                  value={item.grossWt}
                  onChange={(e) => handleSizeChange(index, 'grossWt', e.target.value)}
                  data-row-index={index}
                />
              </div>

              <div className="form-group">
                <label>Net Wt. (g)</label>
                <input
                  type="number"
                  step="0.001"
                  value={item.netWt}
                  onChange={(e) => handleSizeChange(index, 'netWt', e.target.value)}
                  data-row-index={index}
                />
              </div>

              {/* Display calculated averages */}
              {(item.avgGrossWt || item.avgNetWt || item.avgSpecWt) && (
                <div className="calculated-averages">
                  <div className="avg-item">
                    <span>Avg Gross: {item.avgGrossWt}g</span>
                  </div>
                  <div className="avg-item">
                    <span>Avg Net: {item.avgNetWt}g</span>
                  </div>
                  <div className="avg-item">
                    <span>Avg Spec: {item.avgSpecWt}g</span>
                  </div>
                </div>
              )}

              {lotSizes.length > 1 && (
                <button
                  type="button"
                  className="remove-size-btn"
                  onClick={() => {
                    const newSizes = lotSizes.filter((_, i) => i !== index);
                    setLotSizes(newSizes);
                  }}
                  title="Remove this size"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => {
              const availableSizes = getSizeOptions().filter(
                size => !lotSizes.some(item => item.size === size)
              );

              if (availableSizes.length === 0) {
                alert('All available sizes have been added.');
                return;
              }

              setLotSizes([...lotSizes, {
                set: '',
                size: '',
                grossWt: '',
                netWt: '',
                avgGrossWt: '',
                avgNetWt: '',
                avgSpecWt: ''
              }]);
            }}
            className="add-size-btn"
            disabled={lotSizes.length >= getSizeOptions().length}
          >
            + Add More Size
          </button>
        </div>

        {/* Totals Display */}
        <div className="totals-section">
          <h4>Lot Totals</h4>
          <div className="totals-grid">
            <div className="total-item">
              <span>Total Sets:</span>
              <strong>{totals.totalSets}</strong>
            </div>
            <div className="total-item">
              <span>Total Gross Wt:</span>
              <strong>{totals.totalGrossWt.toFixed(3)}g</strong>
            </div>
            <div className="total-item">
              <span>Total Net Wt:</span>
              <strong>{totals.totalNetWt.toFixed(3)}g</strong>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ========== MAIN RENDER ==========

  if (loading) {
    return (
      <div className="catlouge-container">
        <PageHeader title={"Open Market"} />
        <div className="loading-container">
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title={"Open Market"} />
      <div className="catlouge-container">
        {/* Product Count */}
        <div className="product-count">
          <p>{products.length} Products found</p>
        </div>

        {/* Products Grid */}
        <div className="products-grid">
          {products.length === 0 ? (
            <div className="no-products">
              {operatorIds.length === 0
                ? 'No operator linked to your account. Please complete segment registration first.'
                : 'No Open Market products found for your operator.'}
            </div>
          ) : (
            products.map((product, index) => {
              const displayInfo = getProductDisplayInfo(product);

              return (
                <div
                  className="catalog-card"
                  key={`${product.id}-${index}`}
                  onClick={() => handleProductClick(product)}
                >
                  <div className="product-image" >
                    {product.images && product.images.length > 0 && (
                      <img
                        src={product.images[0].url?.trim()}
                        alt={product.productName}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
                        }}
                      />
                    )}
                    {(!product.images || product.images.length === 0) && (
                      <div className="no-image-placeholder">No Image</div>
                    )}

                    {displayInfo.isLot && (
                      <div className="lot-badge">LOT</div>
                    )}
                  </div>

                  <div className="product-info">
                    <h3 className="product-name">{product.productName}</h3>
                    {product.designCode && (
                      <p className="product-category" style={{ fontWeight: 600, color: 'var(--brand-500, #c49b27)' }}>
                        Design: {product.designCode}
                      </p>
                    )}
                    <p className="product-category">
                      {product.segment} / {product.category}
                    </p>
                    <p className="product-category">
                      {product.styleType} / {product.specification}
                    </p>

                    {displayInfo.isLot ? (
                      <>
                        <p className="product-weight-price">
                          {displayInfo.displayWeight} • {displayInfo.displaySets}
                        </p>
                        <p className="product-sizes">
                          Sizes: {displayInfo.displaySizes}
                        </p>
                      </>
                    ) : (
                      <p className="product-weight-price">
                        {displayInfo.displayWeight} {displayInfo.displaySets && `• ${displayInfo.displaySets}`}
                      </p>
                    )}

                    {!displayInfo.isLot && displayInfo.displaySizes && (
                      <p className="product-sizes">
                        Size: {displayInfo.displaySizes}
                      </p>
                    )}

                    <button
                      type="button"
                      className="accept-openmarket-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptProduct(product);
                      }}
                      disabled={pendingAcceptId === product.id}
                    >
                      {pendingAcceptId === product.id ? 'Accepting...' : 'Accept Order'}
                    </button>
                  </div>


                </div>
              );
            })
          )}
        </div>

        {/* Edit Drawer */}
        {drawerOpen && editingProduct && (
          <div className="lot-drawer-overlay" onClick={closeDrawer}>
            <div className="lot-drawer" onClick={e => e.stopPropagation()}>
              <div className="drawer-header">
                <h3>Edit Stock Details</h3>
                <button className="close-btn" onClick={closeDrawer}>×</button>
              </div>

              <div className="drawer-body">
                {renderLotDrawerContent()}
              </div>

              <div className="drawer-footer">
                <button className="cancel-btn" onClick={closeDrawer}>Cancel</button>
                <button
                  className="save-btn"
                  onClick={handleSaveData}
                  disabled={
                    lotSizes.length === 0 || !lotSizes.every(item => item.size && item.set !== '' && item.set !== undefined && item.grossWt !== '' && item.netWt !== '' && parseFloat(item.netWt) <= parseFloat(item.grossWt))
                  }
                >
                  Save & Mark as Ready
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Openmarket;